<template>
    <el-dialog title="文案管理" v-model="visible" width="90%" top="5vh" custom-class="text-manager-dialog" @closed="onClosed">
        <div class="toolbar">
            <div class="filters">
                <el-select v-model="type" size="small" clearable placeholder="全部用途" style="width: 150px;" @change="load">
                    <el-option v-for="t in textTypes" :key="t.value" :label="t.label" :value="t.value"></el-option>
                </el-select>
                <el-input v-model.trim="keyword" size="small" clearable placeholder="搜索 code / 备注 / 文案"
                    style="width: 240px;" @keyup.enter="load" @clear="load"></el-input>
                <el-button size="small" icon="el-icon-search" @click="load">搜索</el-button>
            </div>
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openForm()">新增文案</el-button>
        </div>
        <div class="form-tip" style="margin-bottom: 8px;">配网方案的引导文案、版本提示、方案名、分类名、灯效说明等从这里选择，App 按语言显示对应文案</div>
        <el-table :data="list" border size="small" row-key="code" v-loading="loading" :max-height="tableMaxHeight">
            <el-table-column label="用途" width="110">
                <template v-slot="scope">{{ typeLabel(scope.row.type) }}</template>
            </el-table-column>
            <el-table-column label="备注" min-width="260">
                <template v-slot="scope">
                    <div class="wrap">{{ scope.row.remark || '-' }}</div>
                    <div class="code wrap">{{ scope.row.code }}</div>
                </template>
            </el-table-column>
            <el-table-column label="中文" min-width="240" show-overflow-tooltip>
                <template v-slot="scope">{{ (scope.row.texts || {}).zh || '-' }}</template>
            </el-table-column>
            <el-table-column label="English" min-width="240" show-overflow-tooltip>
                <template v-slot="scope">{{ (scope.row.texts || {}).en || '-' }}</template>
            </el-table-column>
            <el-table-column label="引用数" prop="ref_count" width="70" align="center"></el-table-column>
            <el-table-column label="操作" width="110" align="center">
                <template v-slot="scope">
                    <el-button type="text" size="small" @click="openForm(scope.row)">编辑</el-button>
                    <!-- disabled 按钮不触发鼠标事件，包一层 span 才能显示提示；注释不能放进 el-tooltip，否则触发节点不止一个会报错 -->
                    <el-tooltip v-if="scope.row.ref_count > 0" content="已被方案或分类引用，不能删除" placement="top">
                        <span class="disabled-wrap">
                            <el-button type="text" size="small" disabled>删除</el-button>
                        </span>
                    </el-tooltip>
                    <el-button v-else type="text" size="small" style="color: #f56c6c;" @click="remove(scope.row)">删除</el-button>
                </template>
            </el-table-column>
        </el-table>

        <el-dialog :title="form.code ? '编辑文案' : '新增文案'" v-model="formVisible" width="880px" append-to-body>
            <el-form ref="formRef" :model="form" :rules="rules" label-width="110px" size="small" class="text-form">
                <el-form-item label="用途" prop="type">
                    <el-select v-model="form.type" :disabled="!!form.code" placeholder="请选择用途" style="width: 100%;">
                        <el-option v-for="t in textTypes" :key="t.value" :label="t.label" :value="t.value"></el-option>
                    </el-select>
                    <div v-if="form.code" class="form-tip">code：{{ form.code }}，用途创建后不能修改</div>
                </el-form-item>
                <el-form-item label="备注">
                    <el-input v-model="form.remark" placeholder="便于在下拉中识别，如 GC2 复位引导"></el-input>
                </el-form-item>
                <el-form-item v-for="lang in languages" :key="lang.code" :label="`${lang.name}`"
                    :prop="lang.code === 'en' ? 'texts.en' : ''" :required="lang.code === 'en'">
                    <div class="text-edit">
                        <el-input v-model="form.texts[lang.code]" type="textarea" :autosize="{ minRows: 2, maxRows: 6 }"
                            :placeholder="lang.code === 'en' ? '必填，其他语言未填写时 App 显示英文' : '可选'" class="text-input"></el-input>
                        <div class="text-preview">
                            <div v-if="form.texts[lang.code]" class="rich-preview" v-html="previewHtml(form.texts[lang.code])"></div>
                            <span v-else class="form-tip">预览</span>
                        </div>
                    </div>
                </el-form-item>
                <el-form-item label="格式说明">
                    <div class="form-tip">
                        支持 <code>**加粗**</code>；占位符 <code>{email}</code> 客服邮箱（可点击）、<code>{how_to_reset}</code> 方案复位引导、
                        <code>{color}</code> 复位颜色名、<code>{status}</code> 灯效标题，App 不认识的占位符显示为空；回车换行。
                        旧版 App 会原样显示这些标记
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button size="small" @click="formVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="submit">保存</el-button>
            </template>
        </el-dialog>
    </el-dialog>
</template>

<script>
import pairingApi from './api'
import { richTextPreviewHtml } from './utils'

export default {
    name: 'PairingTextManager',
    props: {
        meta: { type: Object, required: true },
    },
    emits: ['changed'],
    data() {
        return {
            tableMaxHeight: 520,
            visible: false,
            loading: false,
            saving: false,
            changed: false,
            type: '',
            keyword: '',
            list: [],
            formVisible: false,
            form: { code: '', type: '', remark: '', texts: {} },
            rules: {
                type: [{ required: true, message: '请选择用途', trigger: 'change' }],
                'texts.en': [{ required: true, whitespace: true, message: '请输入英文文案', trigger: 'blur' }],
            },
        }
    },
    computed: {
        textTypes() {
            return this.meta.text_types || []
        },
        languages() {
            return this.meta.languages || []
        },
    },
    methods: {
        // type：打开时预先筛选的用途，可选
        open(type) {
            this.type = type || ''
            this.keyword = ''
            this.changed = false
            // 弹窗高度约 90vh，减去标题、工具栏、提示的高度，表格超出时表头固定、内容滚动
            this.tableMaxHeight = Math.max(320, Math.floor(window.innerHeight * 0.9) - 220)
            this.visible = true
            this.load()
        },
        load() {
            this.loading = true
            const params = {}
            if (this.type) params.type = this.type
            if (this.keyword) params.keyword = this.keyword
            pairingApi.TextList(params).then((res) => {
                this.loading = false
                this.list = res.data.data || []
            }).catch(() => {
                this.loading = false
            })
        },
        // 内容已在 richTextPreviewHtml 中转义，只生成 b / span / br 标签
        previewHtml(text) {
            return richTextPreviewHtml(text)
        },
        typeLabel(type) {
            return (this.textTypes.find((t) => t.value === type) || {}).label || type
        },
        openForm(row) {
            const texts = {}
            this.languages.forEach((l) => {
                texts[l.code] = (row && row.texts && row.texts[l.code]) || ''
            })
            this.form = row
                ? { code: row.code, type: row.type, remark: row.remark || '', texts }
                : { code: '', type: this.type || '', remark: '', texts }
            this.formVisible = true
            this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
        },
        submit() {
            this.$refs.formRef.validate((valid) => {
                if (!valid) return
                // 未填写的语言不提交，App 回退到英文
                const texts = {}
                Object.keys(this.form.texts).forEach((k) => {
                    const v = (this.form.texts[k] || '').trim()
                    if (v) texts[k] = v
                })
                const data = { type: this.form.type, remark: this.form.remark.trim(), texts }
                if (this.form.code) data.code = this.form.code
                this.saving = true
                pairingApi.TextSave(data).then((res) => {
                    this.saving = false
                    if (res.data.code == 200) {
                        this.$message.success('保存成功')
                        this.formVisible = false
                        this.changed = true
                        this.load()
                        return
                    }
                    this.$message.error('保存失败' + res.data.msg)
                }).catch(() => {
                    this.saving = false
                })
            })
        },
        remove(row) {
            this.$confirm(`确定删除文案「${row.remark || row.code}」吗？`, '提示', { type: 'warning' }).then(() => {
                pairingApi.TextDelete({ code: row.code }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('删除成功')
                        this.changed = true
                        this.load()
                        return
                    }
                    this.$message.error(res.data.msg)
                })
            }).catch(() => { })
        },
        onClosed() {
            if (this.changed) this.$emit('changed')
        },
    },
}
</script>

<style scoped>
.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.filters {
    display: flex;
    gap: 8px;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.code {
    font-family: monospace;
    font-size: 12px;
    color: #909399;
}

/* 备注和 code 完整显示，长内容换行 */
.wrap {
    white-space: normal;
    word-break: break-all;
    line-height: 18px;
}

.disabled-wrap {
    display: inline-block;
    margin-left: 10px;
}

.text-edit {
    display: flex;
    gap: 8px;
    width: 100%;
}

.text-input {
    flex: 1;
}

.text-preview {
    flex: 1;
    min-width: 0;
    padding: 5px 8px;
    border: 1px dashed #dcdfe6;
    border-radius: 4px;
    background: #fafafa;
    max-height: 140px;
    overflow: auto;
}

.rich-preview {
    font-size: 12px;
    line-height: 18px;
    color: #303133;
    word-break: break-word;
}

.rich-preview ::v-deep(.rich-ph) {
    display: inline-block;
    padding: 0 4px;
    margin: 0 1px;
    border-radius: 3px;
    background: #ecf5ff;
    color: #409eff;
    font-family: monospace;
    line-height: 16px;
}

code {
    padding: 0 3px;
    background: #f2f3f5;
    border-radius: 2px;
    font-family: monospace;
}

.text-form {
    max-height: 60vh;
    overflow: auto;
    padding-right: 10px;
}
</style>
